import { all, call, put, takeLatest } from 'redux-saga/effects';

import * as actions from './providerRequestActions';
import * as services from './providerRequestServices';
import * as types from './providerRequestActionType';
import SweetAlert from '../../../../components/sweetalert/SweetAlert';

// Response casing hasn't been empirically confirmed against a live authenticated
// call for this new controller, and existing sagas in this codebase are
// inconsistent (most check response.success, a couple defensively check
// response?.Success ?? response?.success) - so every accessor below checks both.
const getSuccess = (response) => Boolean(response?.Success ?? response?.success);
const getValue = (response) => response?.Value ?? response?.value ?? response?.Values ?? response?.values;
const getResultMessage = (response) => response?.ResultMessage ?? response?.resultMessage;
const getErrorMessage = (response, fallback) =>
    response?.ErrorMessage ?? response?.errorMessage ?? fallback;

function* fetchProviderRequestsSaga(action) {
    try {
        const response = yield call(services.fetchProviderRequests, action.payload);
        const value = getValue(response);

        if (getSuccess(response) && value) {
            yield put(
                actions.fetchProviderRequestsSuccess({
                    data: value.lstProviderRequest ?? value.LstProviderRequest ?? [],
                    recordsTotal: value.noOfRecords ?? value.NoOfRecords ?? 0,
                }),
            );
            return;
        }

        const errorMessage = getErrorMessage(response, 'Failed to load provider requests.');
        yield put(actions.fetchProviderRequestsFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to load provider requests.';
        yield put(actions.fetchProviderRequestsFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    }
}

function* fetchProviderRequestDropdownsSaga() {
    try {
        const response = yield call(services.fetchProviderRequestDropdowns);
        const value = getValue(response);

        if (getSuccess(response) && value) {
            yield put(actions.fetchProviderRequestDropdownsSuccess(value));
            return;
        }

        const errorMessage = getErrorMessage(response, 'Failed to load provider request dropdowns.');
        yield put(actions.fetchProviderRequestDropdownsFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to load provider request dropdowns.';
        yield put(actions.fetchProviderRequestDropdownsFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    }
}

function* updateProviderRequestStatusSaga(action) {
    try {
        const response = yield call(services.updateProviderRequestStatus, action.payload?.request);

        if (getSuccess(response)) {
            yield put(actions.updateProviderRequestStatusSuccess());

            if (action.payload?.refreshPayload) {
                yield put(actions.fetchProviderRequestsRequest(action.payload.refreshPayload));
            }

            yield call(SweetAlert.success, {
                title: 'Success!',
                text: action.payload?.successMessage || getResultMessage(response) || 'Provider request status updated successfully.',
            });

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, response);
            }
            return;
        }

        const errorMessage = getErrorMessage(response, 'Failed to update provider request status.');
        yield put(actions.updateProviderRequestStatusFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to update provider request status.';
        yield put(actions.updateProviderRequestStatusFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    }
}

function* bulkUpdateProviderRequestTaskSaga(action) {
    try {
        const response = yield call(services.bulkUpdateProviderRequestTask, action.payload?.request);

        if (getSuccess(response)) {
            yield put(actions.bulkUpdateProviderRequestTaskSuccess());

            if (action.payload?.refreshPayload) {
                yield put(actions.fetchProviderRequestsRequest(action.payload.refreshPayload));
            }

            yield call(SweetAlert.success, {
                title: 'Success!',
                text: getResultMessage(response) || 'Provider request Task assignment updated successfully.',
            });

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, response);
            }
            return;
        }

        const errorMessage = getErrorMessage(response, 'Failed to update Task assignment.');
        yield put(actions.bulkUpdateProviderRequestTaskFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to update Task assignment.';
        yield put(actions.bulkUpdateProviderRequestTaskFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    }
}

function* checkProviderRequestAdhocSaga(action) {
    try {
        const response = yield call(services.saveSchedulingCalendarAdhocShifts, {
            ...action.payload?.request,
            IsSave: false,
        });
        const value = getValue(response);

        if (getSuccess(response) && value) {
            yield put(actions.checkProviderRequestAdhocSuccess(value));
            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }
            return;
        }

        const errorMessage = getErrorMessage(response, 'Failed to check shift conflicts.');
        yield put(actions.checkProviderRequestAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to check shift conflicts.';
        yield put(actions.checkProviderRequestAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    }
}

function* saveProviderRequestAdhocSaga(action) {
    try {
        const response = yield call(services.saveSchedulingCalendarAdhocShifts, {
            ...action.payload?.request,
            IsSave: true,
        });
        const value = getValue(response);

        if (getSuccess(response) && value) {
            yield put(actions.saveProviderRequestAdhocSuccess(value));
            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }
            return;
        }

        const errorMessage = getErrorMessage(response, 'Failed to save shifts.');
        yield put(actions.saveProviderRequestAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to save shifts.';
        yield put(actions.saveProviderRequestAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    }
}

function* scheduleRequestTransactionSaga(action) {
    try {
        const response = yield call(services.scheduleRequestTransaction, action.payload?.request);
        const value = getValue(response);

        if (getSuccess(response)) {
            yield put(actions.scheduleRequestTransactionSuccess(value || {}));
            if (action.payload?.request?.IsSave) {
                yield call(SweetAlert.success, {
                    title: 'Success!',
                    text: action.payload?.successMessage || getResultMessage(response) || 'Provider request updated successfully.',
                });
                if (action.payload?.refreshPayload) {
                    yield put(actions.fetchProviderRequestsRequest(action.payload.refreshPayload));
                }
            }
            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value || {});
            }
            return;
        }

        const errorMessage = getErrorMessage(response, 'Failed to process provider request.');
        yield put(actions.scheduleRequestTransactionFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to process provider request.';
        yield put(actions.scheduleRequestTransactionFailure(errorMessage));
        yield call(SweetAlert.error, { title: 'Error!', text: errorMessage });
    }
}

function* providerRequestSaga() {
    yield all([
        takeLatest(types.FETCH_PROVIDER_REQUESTS_REQUEST, fetchProviderRequestsSaga),
        takeLatest(types.FETCH_PROVIDER_REQUEST_DROPDOWNS_REQUEST, fetchProviderRequestDropdownsSaga),
        takeLatest(types.UPDATE_PROVIDER_REQUEST_STATUS_REQUEST, updateProviderRequestStatusSaga),
        takeLatest(types.BULK_UPDATE_PROVIDER_REQUEST_TASK_REQUEST, bulkUpdateProviderRequestTaskSaga),
        takeLatest(types.CHECK_PROVIDER_REQUEST_ADHOC_REQUEST, checkProviderRequestAdhocSaga),
        takeLatest(types.SAVE_PROVIDER_REQUEST_ADHOC_REQUEST, saveProviderRequestAdhocSaga),
        takeLatest(types.SCHEDULE_REQUEST_TRANSACTION_REQUEST, scheduleRequestTransactionSaga),
    ]);
}

export default providerRequestSaga;
